'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface RefreshContextType {
  isRefreshing: boolean;
  lastRefresh: Date | null;
  performHardRefresh: () => Promise<void>;
}

const RefreshContext = createContext<RefreshContextType | undefined>(undefined);

export const useRefresh = () => {
  const context = useContext(RefreshContext);
  if (context === undefined) {
    throw new Error('useRefresh must be used within a RefreshProvider');
  }
  return context;
};

interface RefreshProviderProps {
  children: React.ReactNode;
  checkInterval?: number; // in milliseconds, defaults to 30 minutes
}

export const RefreshProvider: React.FC<RefreshProviderProps> = ({
  children,
  checkInterval = 30 * 60 * 1000, // 30 minutes
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  
  // Function to perform a hard refresh
  const performHardRefresh = useCallback(async () => {
    if (isRefreshing) return; // Prevent multiple refreshes
    
    setIsRefreshing(true);
    
    try {
      if ('caches' in window) {
        const keyList = await caches.keys();
        await Promise.all(keyList.map(key => caches.delete(key)));
      }
      
      // Update last refresh time
      const now = new Date();
      setLastRefresh(now);
      localStorage.setItem('lastAppRefresh', now.toISOString());
      
      // Force page reload
      window.location.reload(true);
    } catch (error) {
      console.error('Error during hard refresh:', error);
      setIsRefreshing(false);
    }
  }, [isRefreshing]);
  
  // Load last refresh time from localStorage on mount
  useEffect(() => {
    const storedLastRefresh = localStorage.getItem('lastAppRefresh');
    if (storedLastRefresh) {
      setLastRefresh(new Date(storedLastRefresh));
    }
  }, []);
  
  // Set up periodic version check for PWA
  useEffect(() => {
    // Function to check for service worker updates
    const checkForUpdates = async () => {
      try {
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          // Create a timestamp to bypass cache
          const timestamp = new Date().getTime();
          
          // Check if the service worker needs to be updated by fetching sw.js
          const response = await fetch(`/sw.js?t=${timestamp}`, {
            headers: { 'Cache-Control': 'no-cache' }
          });
          
          if (response.ok) {
            // Register a new service worker to replace the old one
            const registration = await navigator.serviceWorker.register('/sw.js');
            
            if (registration.waiting) {
              // If there's a waiting service worker, it means there's an update
              console.log('New service worker detected, updating...');
              
              // Store the current page so we can return to it after refresh
              localStorage.setItem('pwaLastPage', window.location.pathname);
              
              // Perform hard refresh after a short delay to allow message to be seen
              setTimeout(() => {
                performHardRefresh();
              }, 1000);
            }
          }
        }
      } catch (error) {
        console.error('Error checking for updates:', error);
      }
    };
    
    // Only set up polling in PWA mode to avoid unnecessary checks in browser
    if (window.matchMedia('(display-mode: standalone)').matches) {
      // Check for updates periodically (every 30 minutes by default)
      const intervalId = setInterval(checkForUpdates, checkInterval);
      
      // Initial check after page load
      setTimeout(checkForUpdates, 5000);
      
      return () => clearInterval(intervalId);
    }
  }, [checkInterval, performHardRefresh]);
  
  const value = {
    isRefreshing,
    lastRefresh,
    performHardRefresh,
  };
  
  return (
    <RefreshContext.Provider value={value}>
      {children}
    </RefreshContext.Provider>
  );
};
